import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { MutationRefetchQueries, ValidationError } from "~/common/types";
import {
  UpdateEvidenceSetDocument,
  type UpdateEvidenceSetMutation,
  type UpdateEvidenceSetMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { EvidenceSetEditFormData } from "~/features/dataImportProjects/types";
import { PropertySchemasMapper } from "~/features/dataImportProjects/utils/propertySchemasMapper";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseEvidenceSetEditParams {
  onSubmit: () => void;
  onError: (error: Pick<ValidationError, "fields">) => void;
  evidenceSetId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
}

export const useEvidenceSetEdit = ({
  onSubmit,
  onError,
  evidenceSetId,
  refetchQueriesOnEdit,
}: UseEvidenceSetEditParams) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateEvidenceSet] = useMutation<UpdateEvidenceSetMutation, UpdateEvidenceSetMutationVariables>(
    UpdateEvidenceSetDocument,
  );
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const submit = useCallback(
    (formData: EvidenceSetEditFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      const propertySchemaSetData = {
        id: formData.propertySchemaSetId,
        propertySchemaAssociations: formData.propertySchemaAssociations,
      };

      const { id: propertySchemaSetId, propertySchemaAssociations: schemas } =
        PropertySchemasMapper.propertySchemaSet.dropIdIfEmpty(propertySchemaSetData);

      updateEvidenceSet({
        refetchQueries: [...refetchQueriesOnEdit],
        variables: {
          input: {
            tenantId,
            id: evidenceSetId,
            name: formData.name,
            propertySchemaSet: {
              id: propertySchemaSetId,
              schemas: schemas ?? [],
            },
          },
        },
      })
        .then((resp) => {
          switch (resp.data?.cnutUpdateEvidenceSet.__typename) {
            case "EvidenceSetUpdateOutput":
              onSubmit();
              toast.success(
                t("feature.common.updatedSuccessfully", {
                  model: "evidenceSet",
                }),
              );
              break;
            case "ValidationError":
              onError(resp.data.cnutUpdateEvidenceSet);
              break;
            default:
              break;
          }
        })
        .catch((e: unknown) => {
          if (e instanceof Error) {
            toast.error(translateError(t, e));
          }
        })
        .finally(() => {
          toast.dismiss(progressToastId);
          setProcessing(false);
        });
    },
    [updateEvidenceSet, evidenceSetId, onSubmit, onError, t, tenantId, refetchQueriesOnEdit],
  );

  return {
    submit,
    processing,
  };
};
